<p-dialog
  [visible]="visible()"
  (visibleChange)="onVisibleChange($event)"
  [modal]="true"
  [style]="{ width: '50rem', maxHeight: '90vh' }"
  [header]="(isRevealingSecret() ? 'SETTINGS_WEBHOOKS.SECRET_REVEAL_TITLE' : dialogTitle()) | translate"
  [draggable]="false"
  [resizable]="false"
  [closable]="!isSaving()"
  (onHide)="onCancel()">
  @if (isRevealingSecret()) {
    <div class="secret-reveal">
      <p-message severity="warn" icon="pi pi-exclamation-triangle" styleClass="w-full">
        {{ 'SETTINGS_WEBHOOKS.SECRET_REVEAL_WARNING' | translate }}
      </p-message>

      <div class="form-field mt-3">
        <label for="webhookSecret">{{ 'SETTINGS_WEBHOOKS.SECRET_LABEL' | translate }}</label>
        <div class="secret-row">
          <input
            pInputText
            id="webhookSecret"
            type="text"
            readonly
            [value]="revealedSecret() ?? ''"
            class="w-full secret-input"
            (focus)="$any($event.target).select()" />
          <p-button
            type="button"
            icon="pi pi-copy"
            severity="secondary"
            [outlined]="true"
            [attr.aria-label]="'SETTINGS_WEBHOOKS.COPY_SECRET' | translate"
            [pTooltip]="'SETTINGS_WEBHOOKS.COPY_SECRET' | translate"
            tooltipPosition="top"
            (onClick)="copySecret()" />
        </div>
        @if (secretCopied()) {
          <small class="copy-feedback copy-feedback-success" role="status">
            {{ 'SETTINGS_WEBHOOKS.COPY_SECRET_SUCCESS' | translate }}
          </small>
        } @else if (secretCopyFailed()) {
          <small class="copy-feedback p-error" role="alert">
            {{ 'SETTINGS_WEBHOOKS.COPY_SECRET_FAILED' | translate }}
          </small>
        }
      </div>
    </div>
  } @else {
  <form class="webhook-form">
    <div class="form-field">
      <label [for]="inputIdPrefix() + 'Name'">
        {{ 'SETTINGS_WEBHOOKS.NAME' | translate }}
        <span class="required">*</span>
      </label>
      <input
        pInputText
        [id]="inputIdPrefix() + 'Name'"
        type="text"
        [ngModel]="formModel().name"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onNameChange($event)"
        [placeholder]="'SETTINGS_WEBHOOKS.NAME_PLACEHOLDER' | translate"
        class="w-full"
        [class.ng-invalid]="!!serverErrors.get('name') || (saveAttempted() && webhookForm.name().invalid())"
        [attr.aria-invalid]="!!serverErrors.get('name') || (saveAttempted() && webhookForm.name().invalid()) || null"
        [attr.aria-describedby]="serverErrors.get('name') ? inputIdPrefix() + 'Name-error' : null" />
      @if (serverErrors.get('name')) {
        <small [id]="inputIdPrefix() + 'Name-error'" class="p-error" role="alert">
          {{ serverErrors.get('name') }}
        </small>
      } @else if (saveAttempted() && webhookForm.name().invalid()) {
        <small class="p-error">{{ 'SETTINGS_WEBHOOKS.NAME_REQUIRED' | translate }}</small>
      }
    </div>

    <div class="form-field">
      <label [for]="inputIdPrefix() + 'Url'">
        {{ 'SETTINGS_WEBHOOKS.URL' | translate }}
        <span class="required">*</span>
      </label>
      <input
        pInputText
        [id]="inputIdPrefix() + 'Url'"
        type="url"
        [ngModel]="formModel().url"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onUrlChange($event)"
        [placeholder]="'SETTINGS_WEBHOOKS.URL_PLACEHOLDER' | translate"
        class="w-full"
        [class.ng-invalid]="showUrlError()"
        [attr.aria-invalid]="showUrlError() || null"
        [attr.aria-describedby]="serverErrors.get('url') ? inputIdPrefix() + 'Url-error' : null" />
      @if (serverErrors.get('url')) {
        <small [id]="inputIdPrefix() + 'Url-error'" class="p-error" role="alert">
          {{ serverErrors.get('url') }}
        </small>
      } @else if (showUrlError()) {
        <small class="p-error">{{ webhookForm.url().errors()[0]?.message ?? 'SETTINGS_WEBHOOKS.URL_REQUIRED' | translate }}</small>
      }
    </div>

    <div class="form-field">
      <div class="flex align-items-center gap-2">
        <p-toggleswitch
          [ngModel]="formModel().enabled"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onEnabledChange($event)" />
        <label [for]="inputIdPrefix() + 'Enabled'">{{ 'SETTINGS_WEBHOOKS.ENABLED' | translate }}</label>
      </div>
    </div>

    <div class="form-field">
      <div class="flex align-items-center gap-2">
        <p-toggleswitch
          [ngModel]="formModel().retry"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onRetryChange($event)" />
        <label [for]="inputIdPrefix() + 'Retry'">{{ 'SETTINGS_WEBHOOKS.RETRY' | translate }}</label>
        <i
          class="pi pi-info-circle text-color-secondary cursor-pointer"
          [pTooltip]="'SETTINGS_WEBHOOKS.RETRY_TOOLTIP' | translate"
          tooltipPosition="top"></i>
      </div>
    </div>

    <div class="form-field">
      <label [for]="inputIdPrefix() + 'EventTypes'">
        {{ 'SETTINGS_WEBHOOKS.EVENT_TYPES' | translate }}
      </label>

      <p-multiselect
        [inputId]="inputIdPrefix() + 'EventTypes'"
        [options]="eventTypeOptions()"
        [ngModel]="formModel().eventTypeIds"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onEventTypeIdsChange($event)"
        [placeholder]="'SETTINGS_WEBHOOKS.SELECT_EVENT_TYPES' | translate"
        [filter]="true"
        [showToggleAll]="true"
        display="chip"
        optionLabel="label"
        optionValue="value"
        [showHeader]="true"
        [maxSelectedLabels]="3"
        [selectedItemsLabel]="'SETTINGS_WEBHOOKS.EVENT_TYPES_SELECTED_COUNT' | translate"
        appendTo="body"
        class="w-full">
        <ng-template let-option pTemplate="item">
          <span class="font-semibold">{{ option.label }}</span>
        </ng-template>
      </p-multiselect>

      <div class="selected-events-container mt-3">
        <span class="text-sm font-semibold mb-2">
          {{ 'SETTINGS_WEBHOOKS.SELECTED_EVENTS' | translate }}
          @if (selectedEventTypesCount() > 0) {
          <span>({{ selectedEventTypesCount() }})</span>
          }
        </span>
        <div class="selected-events-list">
          @if (selectedEventTypesDetails().length > 0) {
          @for (eventType of selectedEventTypesDetails(); track eventType.id) {
          <div class="selected-event-item">
            <span class="font-semibold">{{ eventType.name }} - {{ eventType.category }}</span>
          </div>
          }
          } @else {
          <div class="text-color-secondary text-sm">
            {{ 'SETTINGS_WEBHOOKS.NO_EVENT_SELECTED' | translate }}
          </div>
          }
        </div>
      </div>

      @if (invalidSubscriptionsList().length > 0) {
        <div class="invalid-events-section mt-3">
          <p-message severity="warn" icon="pi pi-exclamation-triangle">
            {{ 'SETTINGS_WEBHOOKS.INVALID_SUBSCRIPTIONS_HELP' | translate }}
          </p-message>
          <div class="invalid-events-list mt-3">
            @for (sub of invalidSubscriptionsList(); track sub) {
              <p-tag severity="warn" [value]="sub" icon="pi pi-times-circle" />
            }
          </div>
        </div>
      }
    </div>
  </form>
  }

  <ng-template #footer>
    @if (isRevealingSecret()) {
      <div class="flex justify-content-end">
        <p-button
          [label]="'SETTINGS_WEBHOOKS.SECRET_ACKNOWLEDGE' | translate"
          severity="primary"
          (onClick)="acknowledgeSecret()" />
      </div>
    } @else {
      <div class="flex gap-2 justify-content-end">
        <p-button
          label="{{ 'GENERAL.CANCEL' | translate }}"
          severity="secondary"
          [outlined]="true"
          [disabled]="isSaving()"
          (onClick)="onCancel()" />
        <p-button
          [label]="'GENERAL.SAVE' | translate"
          severity="primary"
          [disabled]="isSaving() || !canSave()"
          [loading]="isSaving()"
          (onClick)="handleSave()" />
      </div>
    }
  </ng-template>
</p-dialog>
